import * as React from 'react';
import {useEffect, useState} from 'react';
import Api from "../../Api/api";
import {toast} from "react-toastify";
import {formatDate} from "../../helper/formatDate";
import {IoArrowBackSharp} from 'react-icons/io5';
import {useLocation, useNavigate} from "react-router-dom";
import {ClipLoader} from "react-spinners";
import {AES, enc} from "crypto-js";
import {useStateValue} from "../../states/StateProvider";
import {RiArrowDownSFill, RiArrowUpSFill} from "react-icons/ri";
import SortableHeader from "../../components/SortableHeader";
import SmartTooltip from "../../components/SmartTooltip";

const MailHistory = () => {

    const [data, setData] = useState([])
    const [loading, setLoading] = useState(true)
    const navigate = useNavigate();
    const location = useLocation();
    const [{secretKey}, dispatch] = useStateValue();
    const [sortConfig, setSortConfig] = useState({key: 'Datum', direction: 'desc'});
    const [filters, setFilters] = useState({
        Datum: '',
        Modul: '',
        Firma: '',
        Mailtext: '',
        Betreff: '',
        Empfänger: '',
        CC_Empfänger: '',
    });

    useEffect(() => {
        try {
            const decryptedBytes = localStorage.getItem('user') ? AES.decrypt(localStorage.getItem('user'), secretKey) : false;
            const User = JSON.parse(decryptedBytes.toString(enc.Utf8))
            if (User.role !== 'Internal') {
                location.replace('/')
            } else {
                Api().get('/sp_getICMails').then(res => {
                    const cleanedData = res.data.map(item => {
                        // Replace double line breaks with single line breaks
                        let cleanedMailtext = item.Mailtext || '';

                        // Step 1: Normalize all <br> tags to <br> (handle <br/>, <br />, etc.)
                        cleanedMailtext = cleanedMailtext.replace(/<br\s*\/?>/gi, '<br>');

                        // Step 2: Remove all \r\n characters (we only want <br> for line breaks)
                        cleanedMailtext = cleanedMailtext.replace(/\r?\n/g, '');

                        // Step 3: Replace patterns like closing_tag + space + <br> + space with closing_tag + <br>
                        cleanedMailtext = cleanedMailtext.replace(/(<\/[^>]+>)\s*<br>\s*/gi, '$1<br>');

                        // Step 4: Replace multiple consecutive <br> tags (with any whitespace between) with single <br>
                        cleanedMailtext = cleanedMailtext.replace(/(<br>\s*)+/gi, '<br>');

                        // Step 5: Remove trailing <br> tags at the end
                        cleanedMailtext = cleanedMailtext.replace(/(<br>\s*)+$/gi, '');

                        // Step 6: Remove leading <br> tags at the beginning
                        cleanedMailtext = cleanedMailtext.replace(/^(<br>\s*)+/gi, '');

                        // Step 7: Add a single space after each remaining <br> for readability
                        cleanedMailtext = cleanedMailtext.replace(/<br>/gi, '<br> ');

                        // Step 8: Clean up multiple spaces
                        cleanedMailtext = cleanedMailtext.replace(/\s{2,}/g, ' ');

                        // Step 9: Trim the entire string
                        cleanedMailtext = cleanedMailtext.trim();

                        // Remove company name from Betreff if it exists in Firma
                        let cleanedBetreff = item.Betreff || '';
                        if (item.Firma) {
                            cleanedBetreff = cleanedBetreff.replace(new RegExp(item.Firma.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'gi'), '').trim();
                            // Clean up extra spaces, dashes, or separators left behind
                            cleanedBetreff = cleanedBetreff.replace(/^[\s\-–—]+|[\s\-–—]+$/g, '').replace(/\s{2,}/g, ' ');
                        }

                        return {
                            ...item,
                            Mailtext: cleanedMailtext,
                            Betreff: cleanedBetreff
                        };
                    });
                    setLoading(false);
                    setData(cleanedData);
                }).catch(e => {
                    toast.error('etwas ist schief gelaufen!')
                })
            }
        } catch (e) {
            window.location.replace('/anmeldung')
        }
    }, []);

    const filteredAndSortedData = React.useMemo(() => {
        let filtered = [...data];

        // Apply filters
        Object.entries(filters).forEach(([key, value]) => {
            if (value) {
                filtered = filtered.filter(item => {
                    let fieldValue = item[key] || '';

                    if (key === 'Datum') {
                        fieldValue = formatDate(item[key], false); // formatDate usage here!
                    }

                    return fieldValue.toString().toLowerCase().includes(value.toLowerCase());
                });
            }
        });

        // Apply sorting
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                const aVal = sortConfig.key === 'Datum' ? new Date(a[sortConfig.key]) : (a[sortConfig.key] || '').toString().toLowerCase();
                const bVal = sortConfig.key === 'Datum' ? new Date(b[sortConfig.key]) : (b[sortConfig.key] || '').toString().toLowerCase();

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [data, sortConfig, filters]);


    const requestSort = (key, direction = null) => {
        let newDirection = 'asc';

        if (direction) {
            newDirection = direction;
        } else {
            // Toggle if no direction given (fallback)
            if (sortConfig.key === key && sortConfig.direction === 'asc') {
                newDirection = 'desc';
            }
        }

        setSortConfig({key, direction: newDirection});
    };

    const SortArrow = ({field}) => {
        const isActive = sortConfig.key === field;
        const isAsc = isActive && sortConfig.direction === 'asc';
        const isDesc = isActive && sortConfig.direction === 'desc';

        return (<span className='inline-flex flex-col items-center ml-1 leading-none'>
                <span>
                    <RiArrowUpSFill
                        size='22px'
                        className={isAsc ? 'text-black' : 'text-gray-400'}
                        style={{marginBottom: '-2px'}}
                    />
                </span>
                <span className={`-mt-3.5`}>
                    <RiArrowDownSFill
                        size='22px'
                        className={isDesc ? 'text-black' : 'text-gray-400'}
                        style={{marginBottom: '-2px'}}
                    />
                </span>
        </span>);
    };

    const handleFilterChange = (field, value) => {
        setFilters(prev => ({
            ...prev,
            [field]: value,
        }));
    };

    return (
        <div className='dashboardContainer'>
            <h2 className='text-2xl lg:text-left'>ServiceMail Verlauf</h2>

            <div className='float-right'>
                <a title='vorherige Seite' onClick={() => navigate(-1)} className='cursor-pointer'>
                    <IoArrowBackSharp size={'30px'} />
                </a>
            </div>

            {/* Reset Filters Button */}
            {Object.values(filters).some(val => val.trim() !== '') && (
                <div className="mb-3 flex justify-end">
                    <button
                        onClick={() => setFilters({ Datum: '', Betreff: '', Empfänger: '', CC_Empfänger: '', Modul: '', Firma: '', Mailtext: '' })}
                        className="mr-3 justify-center rounded-md border border-offWhite shadow-sm px-4 opacity-80 py-2 bg-cancel text-sm font-medium text-white focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                    >Filters zurücksetzen</button>
                </div>
            )}

            {loading ? (
                <div className='centerItemsAbsolute'>
                    <ClipLoader size={'40px'} color={'black'} />
                </div>
            ) : (
                <table className='min-w-full table-fixed text-left bg-white rounded-xl'>
                    <thead className="sticky bg-white top-0 z-10 whitespace-nowrap border-y border-silver border-x-0">
                    <tr className='text-sm text-grey pl-1.5'>
                        <th className="w-[5%]">
                            <SortableHeader label="Datum" field="Datum" sortConfig={sortConfig} requestSort={requestSort} />
                        </th>
                        <th className="w-[5%]">
                            <SortableHeader label="Modul" field="Modul" sortConfig={sortConfig} requestSort={requestSort} />
                        </th>
                        <th className="w-[10%]">
                            <SortableHeader label="Firma" field="Firma" sortConfig={sortConfig} requestSort={requestSort} />
                        </th>
                        <th className="w-[20%]">
                            <SortableHeader label="Betreff" field="Betreff" sortConfig={sortConfig} requestSort={requestSort} />
                        </th>
                        <th className="w-[40%]">Mailtext</th>
                        <th className="w-[10%]">
                            <SortableHeader label="Empfänger" field="Empfänger" sortConfig={sortConfig} requestSort={requestSort} />
                        </th>
                        <th className="w-[10%]">
                            <SortableHeader label="CC Empfänger" field="CC_Empfänger" sortConfig={sortConfig} requestSort={requestSort} />
                        </th>
                    </tr>
                    <tr className="text-sm">
                        <th className="w-[5%]">
                            <input
                                type="text"
                                placeholder="Filter Datum"
                                value={filters.Datum}
                                onChange={e => handleFilterChange('Datum', e.target.value)}
                                className="mt-1 text-sm border rounded w-full px-2 py-0.5"
                            />
                        </th>
                        <th className="w-[5%]">
                            <input
                                type="text"
                                placeholder="Filter Modul"
                                value={filters.Modul}
                                onChange={e => handleFilterChange('Modul', e.target.value)}
                                className="mt-1 text-sm border rounded w-full px-2 py-0.5"
                            />
                        </th>
                        <th className="w-[10%]">
                            <input
                                type="text"
                                placeholder="Filter Firma"
                                value={filters.Firma}
                                onChange={e => handleFilterChange('Firma', e.target.value)}
                                className="mt-1 text-sm border rounded w-full px-2 py-0.5"
                            />
                        </th>
                        <th className="w-[20%]">
                            <input
                                type="text"
                                placeholder="Filter Betreff"
                                value={filters.Betreff}
                                onChange={e => handleFilterChange('Betreff', e.target.value)}
                                className="mt-1 text-sm border rounded w-full px-2 py-0.5"
                            />
                        </th>
                        <th className="w-[40%]">
                            <input
                                type="text"
                                placeholder="Filter Mailtext"
                                value={filters.Mailtext}
                                onChange={e => handleFilterChange('Mailtext', e.target.value)}
                                className="mt-1 text-sm border rounded w-full px-2 py-0.5"
                            />
                        </th>
                        <th className="w-[10%]">
                            <input
                                type="text"
                                placeholder="Filter Empfänger"
                                value={filters.Empfänger}
                                onChange={e => handleFilterChange('Empfänger', e.target.value)}
                                className="mt-1 text-sm border rounded w-full px-2 py-0.5"
                            />
                        </th>
                        <th className="w-[10%]">
                            <input
                                type="text"
                                placeholder="Filter CC"
                                value={filters.CC_Empfänger}
                                onChange={e => handleFilterChange('CC_Empfänger', e.target.value)}
                                className="mt-1 text-sm border rounded w-full px-2 py-0.5"
                            />
                        </th>
                    </tr>
                    </thead>
                    <tbody>
                    {filteredAndSortedData.map((d, index) => (
                        <tr key={index} className='border-y border-silver border-x-0 text-sm hover:bg-silver'>
                            <td className='text-gray-900 font-light pr-3'>
                                {formatDate(d.Datum, false)}
                            </td>
                            <td className='text-gray-900 font-light pr-3'>
                                {d.Modul || ''}
                            </td>
                            <td className='text-gray-900 font-light pr-3'>
                                {d.Firma || ''}
                            </td>
                            <td className='text-gray-900 font-light pr-3'>
                                <span dangerouslySetInnerHTML={{ __html: d.Betreff }} />
                            </td>
                            <SmartTooltip
                                content={d.Mailtext}
                                placement="auto"
                                maxWidth={520}
                                allowHtml={true}
                            >
                                <td className='text-gray-900 font-light pr-3'>
                                    <div
                                        className="line-clamp-3 overflow-hidden"
                                        dangerouslySetInnerHTML={{ __html: d.Mailtext || '' }}
                                    />
                                </td>
                            </SmartTooltip>
                            <td className='text-gray-900 font-light pr-3'>
                                {d.Empfänger}
                            </td>
                            <td className='text-gray-900 font-light pr-3'>
                                {d.CC_Empfänger}
                            </td>
                        </tr>
                    ))}
                    </tbody>
                </table>
            )}
        </div>
    );
};

export default MailHistory
